// Transitions PR-1 — cancel a transition with a required reason. Canceling a
// SCHEDULED payoff-family transition additionally requires the explicit
// irrevocable-notice override (§2.5) — surfaced as a checkbox with red
// consequence copy; the server audits the override loudly.

import { useState } from "react";
import type { FormEvent } from "react";
import { Button, Label, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { useChangeLoanTransitionStatus } from "@/hooks/portfolioAdmin/useLoanTransitions";

export function CancelTransitionModal({
  loanId,
  transitionId,
  requiresOverride,
  open,
  onClose,
}: {
  loanId: string;
  transitionId: string;
  /** True for a payoff-family transition in Scheduled — the notice is irrevocable. */
  requiresOverride: boolean;
  open: boolean;
  onClose: () => void;
}) {
  const mutation = useChangeLoanTransitionStatus(loanId);
  const [reason, setReason] = useState("");
  const [override, setOverride] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await mutation.mutateAsync({
        transitionId,
        body: {
          targetStatus: "Canceled",
          reason: reason.trim(),
          overrideIrrevocableNotice: requiresOverride && override,
        },
      });
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancel transition"
      description="Closes this transition. The record stays in the loan's history."
    >
      <form onSubmit={submit} className="flex flex-col gap-space-3" data-testid="cancel-transition-form">
        <div>
          <Label htmlFor="cancel-reason">Reason (required)</Label>
          <Textarea
            id="cancel-reason"
            rows={2}
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="cancel-reason-input"
          />
        </div>
        {requiresOverride && (
          <div
            className="rounded border border-status-error/40 bg-status-error/5 p-space-2"
            data-testid="cancel-override-warning"
          >
            <p className="text-small text-status-error font-semibold m-0">
              The Prepayment Notice is irrevocable under the note terms (§2.5).
            </p>
            <p className="text-small text-gray-mid m-0 mt-space-1">
              Canceling a scheduled payoff is a staff override and is loudly
              audited.
            </p>
            <label className="flex items-start gap-space-1 mt-space-2 text-small">
              <input
                type="checkbox"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
                data-testid="cancel-override-checkbox"
              />
              <span>
                I understand the notice is irrevocable and I am overriding it.
              </span>
            </label>
          </div>
        )}
        {error && <p className="text-small text-status-error m-0">{error}</p>}
        <div className="flex justify-end gap-space-2">
          <Button type="button" variant="secondary" onClick={onClose}>
            Keep transition
          </Button>
          <Button
            type="submit"
            disabled={mutation.isPending || !reason.trim() || (requiresOverride && !override)}
            data-testid="cancel-transition-submit"
          >
            {mutation.isPending ? "Canceling…" : "Cancel transition"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
